import PropTypes from "prop-types";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FaGoogle, FaMicrosoft, FaYahoo, FaCalendarPlus, FaApple, FaDownload } from "react-icons/fa";
import Button from "./Button";

const AddToCalendarCustomButton = ({
  name,
  description = "",
  location = "",
  startDate,
  endDate,
  startTime,
  endTime,
  timeZone = "America/New_York",
  label,
  bgColor = "var(--red)",
  color,
  hoverBgColor = "white",
  hoverColor,
  size = "medium",
  width = "regular",
}) => {
  const { t } = useTranslation();

  // Helper function to format date/time for different calendar services
  const formatDateTime = (date, time) => {
    const dateTime = `${date}T${time}:00`;
    const dt = new Date(dateTime);

    // For Google/Yahoo: Format as YYYYMMDDTHHmmssZ (UTC)
    const utcString = dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    // For Outlook: ISO format
    const isoString = dt.toISOString();

    // For ICS: Format as YYYYMMDDTHHmmss (local time)
    const localString = dateTime.replace(/[-:]/g, '');

    return { utcString, isoString, localString };
  };

  const start = formatDateTime(startDate, startTime, timeZone);
  const end = formatDateTime(endDate, endTime, timeZone);

  // Strip markdown syntax to get plain text for calendar services that don't render markdown
  const stripMarkdown = (md) =>
    md
      .replace(/!\[.*?\]\(.*?\)/g, '')   // images
      .replace(/#{1,6}\s*/g, '')          // headings
      .replace(/(\*\*|__)(.*?)\1/g, '$2') // bold
      .replace(/(\*|_)(.*?)\1/g, '$2')    // italic
      .replace(/`{1,3}[^`]*`{1,3}/g, '')  // code
      .replace(/^\s*[-*+]\s+/gm, '')      // list items
      .replace(/\n{2,}/g, ' ')            // multiple newlines → space
      .replace(/\n/g, ' ')                // single newlines → space
      .trim();

  const plainDescription = stripMarkdown(description);

  // Track calendar clicks
  const trackCalendarClick = (calendarType) => {
    if (process.env.REACT_APP_GA_TRACKING === "true" && window.dataLayer) {
      window.dataLayer.push({
        event: 'calendar_add',
        calendar_type: calendarType,
        event_name: name,
      });
    }
  };

  // Generate calendar URLs
  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(name)}&dates=${start.utcString}/${end.utcString}&details=${encodeURIComponent(plainDescription)}&location=${encodeURIComponent(location)}`;

  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(name)}&startdt=${start.isoString}&enddt=${end.isoString}&body=${encodeURIComponent(plainDescription)}&location=${encodeURIComponent(location)}`;

  const yahooUrl = `https://calendar.yahoo.com/?v=60&title=${encodeURIComponent(name)}&st=${start.utcString}&et=${end.utcString}&desc=${encodeURIComponent(plainDescription)}&in_loc=${encodeURIComponent(location)}`;

  // Generate ICS file content
  const generateICS = () => {
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Playground//AddToCalendar//EN',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@playground.ca`,
      `DTSTART:${start.localString}`,
      `DTEND:${end.localString}`,
      `SUMMARY:${name}`,
      `DESCRIPTION:${plainDescription.replace(/\n/g, '\\n')}`,
      `LOCATION:${location}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    return icsContent;
  };

  // Download ICS file for Apple
  const downloadICS = (e) => {
    e.preventDefault();
    trackCalendarClick('apple');
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download generic ICS file
  const downloadGenericICS = (e) => {
    e.preventDefault();
    trackCalendarClick('download');
    const icsContent = generateICS();
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const buttonLabel = (
    <>
      <FaCalendarPlus style={{ marginRight: '0.5rem' }} />
      {label || t("calendar.addToCalendar.button")}
    </>
  );

  return (
    <StyledDropdown>
      <Dropdown.Toggle
        as={Button}
        label={buttonLabel}
        bgColor={bgColor}
        color={color}
        hoverBgColor={hoverBgColor}
        hoverColor={hoverColor}
        size={size}
        width={width}
      />
      <Dropdown.Menu align="end">
        <Dropdown.Item onClick={downloadICS}>
          <FaApple className="calendar-icon" /> {t("calendar.addToCalendar.apple")}
        </Dropdown.Item>
        <Dropdown.Item
          href={googleCalendarUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('google')}
        >
          <FaGoogle className="calendar-icon" /> {t("calendar.addToCalendar.google")}
        </Dropdown.Item>
        <Dropdown.Item
          href={outlookUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('outlook')}
        >
          <FaMicrosoft className="calendar-icon" /> {t("calendar.addToCalendar.outlook")}
        </Dropdown.Item>
        <Dropdown.Item
          href={yahooUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackCalendarClick('yahoo')}
        >
          <FaYahoo className="calendar-icon" /> {t("calendar.addToCalendar.yahoo")}
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={downloadGenericICS}>
          <FaDownload className="calendar-icon" /> {t("calendar.addToCalendar.download")}
        </Dropdown.Item>
      </Dropdown.Menu>
    </StyledDropdown>
  );
};

const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  position: relative;
  z-index: 9999;

  .dropdown-toggle::after {
    display: none;
  }

  .dropdown-menu {
    background-color: var(--white);
    border: 1px solid var(--lightgrey);
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    min-width: 250px;
    z-index: 9999;

    .dropdown-item {
      color: var(--black);
      padding: 0.75rem 1.25rem;
      transition: background 0.2s ease-in-out;
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .calendar-icon {
        font-size: 1.1rem;
        flex-shrink: 0;
      }

      &:hover,
      &:focus {
        background-color: var(--red);
        color: var(--white);
      }

      &:active {
        background-color: var(--red);
        color: var(--white);
      }
    }

    .dropdown-divider {
      border-top: 1px solid var(--lightgrey);
      margin: 0.5rem 0;
    }
  }
`;

AddToCalendarCustomButton.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  location: PropTypes.string,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  endTime: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  label: PropTypes.string,
  bgColor: PropTypes.string,
  color: PropTypes.string,
  hoverBgColor: PropTypes.string,
  hoverColor: PropTypes.string,
  size: PropTypes.string,
  width: PropTypes.string,
};

export default AddToCalendarCustomButton;

